/**
 * Benchmark bucket rules. Keep these rule for rule in step with the backend classifier in
 * src/budget-benchmark/budget-bucket-classification.ts, so the user's own verdict and the peer
 * benchmark always bucket an account the same way.
 */
import { getBudgetItemsEstimate } from "./BudgetItemUtils";
import type { BudgetCategory } from "./types";

export type BenchmarkBucket = "atl" | "production" | "post" | "other";

export interface BucketShare {
  bucket: BenchmarkBucket;
  estimate: number;
  sharePercent: number;
}

export interface ClassifiableAccount {
  /** Account (sub-category) name, for example "EDITING". */
  name?: string;
  account_number?: string;
  items: Array<{ account_number?: string }>;
}

export interface ClassifiableCategory {
  name: string;
  /**
   * The category's stored ATL/BTL section. Saved categories always have one; parsed imports
   * that are not saved yet do not, and fall back to the category-level estimate.
   */
  budget_section?: string | null;
  sub_categories: ClassifiableAccount[];
}

export interface CategoryClassification {
  bucket: BenchmarkBucket;
  method: "account_number" | "name_keyword" | "default";
}

export interface AccountClassification {
  bucket: BenchmarkBucket;
  method:
    | "atl_section"
    | "legacy_atl_name"
    | "account_name"
    | "account_number"
    | "category_name"
    | "btl_default"
    | "category_estimate";
}

const BENCHMARK_BUCKETS: BenchmarkBucket[] = ["atl", "production", "post", "other"];

// ---------------------------------------------------------------------------------------------
// Category-level estimate, used only when a category has no stored section (unsaved imports).
// ---------------------------------------------------------------------------------------------

const ATL_KEYWORDS = [
  "above the line",
  "above-the-line",
  "story",
  "rights",
  "writer",
  "producer",
  "director",
  "cast",
  "talent",
  "principal",
  "development",
];

const POST_KEYWORDS = [
  "post production",
  "post-production",
  "postproduction",
  "editorial",
  "vfx",
  "visual effects",
  "sound",
  "music",
  "color",
  "deliverable",
  "mix",
  "master",
];

const OTHER_KEYWORDS = [
  "insurance",
  "completion bond",
  "bond",
  "contingency",
  "fringe",
  "legal",
  "general expense",
  "distribution",
  "publicity",
];

function parseAccountNumber(value: string | undefined | null): number | null {
  if (!value) return null;
  const match = value.trim().match(/^(\d+)/);
  if (!match) return null;
  const parsed = Number.parseInt(match[1], 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/** In a 4-digit chart of accounts, numbers below this are above the line. */
const FIRST_BELOW_THE_LINE_ACCOUNT = 2000;

function bucketFromAccountNumber(accountNumber: number): BenchmarkBucket {
  if (accountNumber < FIRST_BELOW_THE_LINE_ACCOUNT) return "atl";
  if (accountNumber < 4500) return "production";
  if (accountNumber < 6000) return "post";
  return "other";
}

function normalizeName(value: string): string {
  return value.trim().toLowerCase();
}

function matchesKeyword(name: string, keywords: string[]): boolean {
  const normalized = normalizeName(name);
  return keywords.some((keyword) => normalized.includes(keyword));
}

function bucketFromName(name: string): BenchmarkBucket | null {
  if (matchesKeyword(name, OTHER_KEYWORDS)) return "other";
  if (matchesKeyword(name, ATL_KEYWORDS)) return "atl";
  if (matchesKeyword(name, POST_KEYWORDS)) return "post";
  if (matchesKeyword(name, ["production", "below the line", "below-the-line", "btl"])) {
    return "production";
  }
  return null;
}

function collectAccountNumbers(category: ClassifiableCategory): number[] {
  const numbers: number[] = [];

  category.sub_categories.forEach((subCategory) => {
    const subNumber = parseAccountNumber(subCategory.account_number);
    if (subNumber != null) numbers.push(subNumber);

    subCategory.items.forEach((item) => {
      const itemNumber = parseAccountNumber(item.account_number);
      if (itemNumber != null) numbers.push(itemNumber);
    });
  });

  return numbers;
}

function bucketFromAccountNumbers(accountNumbers: number[]): BenchmarkBucket | null {
  if (accountNumbers.length === 0) return null;

  const votes = new Map<BenchmarkBucket, number>();
  accountNumbers.forEach((accountNumber) => {
    const bucket = bucketFromAccountNumber(accountNumber);
    votes.set(bucket, (votes.get(bucket) ?? 0) + 1);
  });

  let winner: BenchmarkBucket = "production";
  let winnerCount = -1;
  votes.forEach((count, bucket) => {
    if (count > winnerCount) {
      winner = bucket;
      winnerCount = count;
    }
  });

  return winner;
}

/** Category-level estimate for a category with no stored section (unsaved imports). */
export function classifyCategoryBucket(category: ClassifiableCategory): CategoryClassification {
  const accountNumbers = collectAccountNumbers(category);
  const fromAccounts = bucketFromAccountNumbers(accountNumbers);
  if (fromAccounts) {
    return { bucket: fromAccounts, method: "account_number" };
  }

  const fromName = bucketFromName(category.name);
  if (fromName) {
    return { bucket: fromName, method: "name_keyword" };
  }

  return { bucket: "production", method: "default" };
}

// ---------------------------------------------------------------------------------------------
// Account-level rules for saved budgets, which always carry budget_section.
// Names are matched on whole words, so "Compost", "Poster" and "Postage" never read as post.
// ---------------------------------------------------------------------------------------------

/** Lowercase words, so "POST-PRODN." gives ["post", "prodn"]. */
function wordsOf(name: string | undefined | null): string[] {
  return String(name ?? "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function hasPhrase(words: string[], phrase: string): boolean {
  return ` ${words.join(" ")} `.includes(` ${phrase} `);
}

function hasWord(words: string[], candidates: readonly string[]): boolean {
  return words.some((word) => candidates.includes(word));
}

function hasWordStartingWith(words: string[], prefixes: readonly string[]): boolean {
  return words.some((word) => prefixes.some((prefix) => word.startsWith(prefix)));
}

const GENERIC_OTHER_CORE_WORDS = [
  "other",
  "others",
  "misc",
  "miscellaneous",
  "miscelaneous",
  "indirect",
  "unclassified",
  "unclassifed",
] as const;

const GENERIC_OTHER_FILLER_WORDS = [
  ...GENERIC_OTHER_CORE_WORDS,
  "total",
  "sub",
  "subtotal",
  "below",
  "the",
  "line",
  "btl",
  "and",
  "general",
  "cost",
  "costs",
  "charge",
  "charges",
  "expense",
  "expenses",
  "exp",
  "exps",
  "items",
] as const;

/**
 * A summary label such as "OTHER", "Others", "Indirect costs", "MISCELLANEOUS EXP" or
 * "Total Below The Line Other". "Other Production Costs" and "Other Post Production" are not.
 */
function isGenericOtherLabel(words: string[]): boolean {
  return (
    words.length > 0 &&
    words.every((word) => (GENERIC_OTHER_FILLER_WORDS as readonly string[]).includes(word)) &&
    hasWord(words, GENERIC_OTHER_CORE_WORDS)
  );
}

/** Insurance, bond, fringes, legal, finance, admin, overhead, publicity, marketing, amortization. */
const OTHER_WORD_PREFIXES = [
  "insur",
  "fringe",
  "contingenc",
  "guarantor",
  "legal",
  "accounting",
  "financ",
  "administrat",
  "overhead",
  "publicity",
  "marketing",
  "promotion",
  "amort",
] as const;

function isOtherName(words: string[]): boolean {
  return (
    isGenericOtherLabel(words) ||
    hasWordStartingWith(words, OTHER_WORD_PREFIXES) ||
    hasWord(words, ["bond"]) ||
    (hasWord(words, ["general"]) && hasWordStartingWith(words, ["expens"]))
  );
}

const POST_MARKER_WORDS = ["post", "postproduction"] as const;
const PRODUCTION_CONTEXT_WORDS = ["production", "prod", "prodn", "shoot", "shooting"] as const;
const POST_WORDS = [
  "vfx",
  "music",
  "title",
  "titles",
  "graphics",
  "projection",
  "lab",
  "labs",
  "laboratory",
  "versioning",
  "mastering",
  "grading",
  "colorist",
  "colorists",
] as const;
const POST_WORD_PREFIXES = [
  "edit",
  "mix",
  "optical",
  "deliver",
  "preview",
  "conform",
  "dub",
] as const;
const POST_PHRASES = ["visual effects", "digital intermediate"] as const;

function isPostName(words: string[]): boolean {
  if (hasWord(words, POST_MARKER_WORDS) || words.some((word) => word.startsWith("postprod"))) {
    return true;
  }
  // "PRODUCTION SOUND", "PRODUCTION FILM & LAB", "VISUAL EFFECTS (PRODUCTION)" stay production.
  if (hasWord(words, PRODUCTION_CONTEXT_WORDS)) return false;
  return (
    hasWord(words, POST_WORDS) ||
    hasWordStartingWith(words, POST_WORD_PREFIXES) ||
    POST_PHRASES.some((phrase) => hasPhrase(words, phrase))
  );
}

const LEGACY_ATL_WORDS = [
  "story",
  "script",
  "scripts",
  "screenplay",
  "scenario",
  "writer",
  "writers",
  "writing",
  "rights",
  "producer",
  "producers",
  "director",
  "directors",
  "cast",
  "talent",
  "development",
] as const;
/** Crew and extras whose names contain an ATL word: "EXTRA TALENT", "ASST DIRECTORS". */
const LEGACY_ATL_EXCLUDED_WORDS = [
  "extra",
  "extras",
  "crowd",
  "crowds",
  "background",
  "asst",
  "assistant",
  "assistants",
] as const;

/**
 * For legacy budgets that have no atl category (every account was saved under btl), an account
 * counts as ATL when its own name says so. "DIRECTION" alone counts; "ART DIRECTION" does not.
 */
function isLegacyAtlName(words: string[]): boolean {
  if (hasPhrase(words, "below the line") || hasWord(words, ["btl"])) return false;
  if (hasPhrase(words, "above the line") || hasWord(words, ["atl"])) return true;
  if (hasWord(words, LEGACY_ATL_EXCLUDED_WORDS) || hasWordStartingWith(words, ["standin"])) {
    return false;
  }
  return hasWord(words, LEGACY_ATL_WORDS) || words.join(" ") === "direction";
}

function classifyBelowTheLineAccount(
  category: ClassifiableCategory,
  account: ClassifiableAccount,
  inferAtlFromNames: boolean
): AccountClassification {
  const accountWords = wordsOf(account.name);
  if (inferAtlFromNames && isLegacyAtlName(accountWords)) {
    return { bucket: "atl", method: "legacy_atl_name" };
  }
  if (isOtherName(accountWords)) return { bucket: "other", method: "account_name" };
  if (isPostName(accountWords)) return { bucket: "post", method: "account_name" };

  // Only 4-digit numbers of 2000 and up carry a bucket. "NN-00" numbers (every RivetAI
  // template) parse below 2000 and say nothing about the section.
  const accountNumber = parseAccountNumber(account.account_number);
  if (accountNumber != null && accountNumber >= FIRST_BELOW_THE_LINE_ACCOUNT) {
    return { bucket: bucketFromAccountNumber(accountNumber), method: "account_number" };
  }

  const categoryWords = wordsOf(category.name);
  if (isOtherName(categoryWords)) return { bucket: "other", method: "category_name" };
  if (isPostName(categoryWords)) return { bucket: "post", method: "category_name" };

  return { bucket: "production", method: "btl_default" };
}

/**
 * Buckets for every account of a budget, as `result[categoryIndex][accountIndex]`.
 *
 * - An atl category puts every account in ATL.
 * - A btl category is classified account by account (see classifyBelowTheLineAccount).
 * - A budget with no atl category is legacy: its accounts named like ATL roles count as ATL.
 * - A category with no stored section uses the category-level estimate for all its accounts.
 */
export function classifyBudgetAccounts(
  categories: ClassifiableCategory[]
): AccountClassification[][] {
  const inferAtlFromNames = !categories.some((category) => category.budget_section === "atl");

  return categories.map((category) => {
    if (category.budget_section === "atl") {
      return category.sub_categories.map(
        (): AccountClassification => ({ bucket: "atl", method: "atl_section" })
      );
    }
    if (category.budget_section === "btl") {
      return category.sub_categories.map((account) =>
        classifyBelowTheLineAccount(category, account, inferAtlFromNames)
      );
    }
    const { bucket } = classifyCategoryBucket(category);
    return category.sub_categories.map(
      (): AccountClassification => ({ bucket, method: "category_estimate" })
    );
  });
}

/**
 * Bucket shares on the same basis as the peer benchmark: each account's line items, with no
 * computed fringes, plus contingency on that total folded into other.
 */
export function buildBucketShares(
  categories: BudgetCategory[],
  contingencyPercentage: number
): { shares: BucketShare[]; grandTotal: number } {
  const estimates = new Map<BenchmarkBucket, number>(
    BENCHMARK_BUCKETS.map((bucket) => [bucket, 0])
  );
  const accountBuckets = classifyBudgetAccounts(categories);

  categories.forEach((category, categoryIndex) => {
    category.sub_categories.forEach((account, accountIndex) => {
      const { bucket } = accountBuckets[categoryIndex][accountIndex];
      estimates.set(bucket, (estimates.get(bucket) ?? 0) + getBudgetItemsEstimate(account.items));
    });
  });

  const categoriesTotal = BENCHMARK_BUCKETS.reduce(
    (sum, bucket) => sum + (estimates.get(bucket) ?? 0),
    0
  );
  const contingencyEstimate =
    contingencyPercentage > 0 ? (contingencyPercentage / 100) * categoriesTotal : 0;
  estimates.set("other", (estimates.get("other") ?? 0) + contingencyEstimate);

  const grandTotal = categoriesTotal + contingencyEstimate;
  const toShare = (estimate: number) => (grandTotal > 0 ? (estimate / grandTotal) * 100 : 0);

  const shares = BENCHMARK_BUCKETS.map((bucket) => ({
    bucket,
    estimate: estimates.get(bucket) ?? 0,
    sharePercent: toShare(estimates.get(bucket) ?? 0),
  }));

  return { shares, grandTotal };
}

/** Categories with no stored section whose category-level estimate fell back to the default. */
export function countAmbiguousClassifications(categories: BudgetCategory[]): number {
  return categories.filter(
    (category) =>
      category.budget_section !== "atl" &&
      category.budget_section !== "btl" &&
      classifyCategoryBucket(category).method === "default"
  ).length;
}

export type BenchmarkClassificationConfidence = "high" | "medium" | "low";

export interface BenchmarkClassificationAssessment {
  confidence: BenchmarkClassificationConfidence;
  ambiguousCategoryCount: number;
  categoryCount: number;
}

const DEFAULT_AMBIGUOUS_THRESHOLD = 0.25;

export function assessBenchmarkClassification(
  categories: BudgetCategory[],
  ambiguousThreshold = DEFAULT_AMBIGUOUS_THRESHOLD
): BenchmarkClassificationAssessment {
  const categoryCount = categories.length;
  if (categoryCount === 0) {
    return { confidence: "low", ambiguousCategoryCount: 0, categoryCount: 0 };
  }

  const ambiguousCategoryCount = countAmbiguousClassifications(categories);
  const ambiguousShare = ambiguousCategoryCount / categoryCount;

  if (ambiguousShare === 0) {
    return { confidence: "high", ambiguousCategoryCount, categoryCount };
  }
  if (ambiguousShare < ambiguousThreshold) {
    return { confidence: "medium", ambiguousCategoryCount, categoryCount };
  }
  return { confidence: "low", ambiguousCategoryCount, categoryCount };
}
